import { useEffect, useRef, useState } from 'react';
import { pedirCitas, type CitaDeFicha } from '../../../common/services/catalogo.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { ENLACE_A_EDITOR } from '../../../common/utils/enlaces.js';

/** De dónde sale el dato enlazado: `?hecho=<id>` o el ancla `#p16`. `null` si el enlace no trae ninguno. */
export function filtroDelEnlace(search: string, hash: string): { readonly hecho: string } | { readonly pagina: number } | null {
  const hecho = new URLSearchParams(search).get('hecho');
  if (hecho !== null && /^[0-9a-f-]{32,36}$/u.test(hecho)) return { hecho };
  const pagina = /^#p(\d{1,4})$/u.exec(hash);
  return pagina === null ? null : { pagina: Number(pagina[1]) };
}

/**
 * Lo que vino a ver quien llega con un enlace a un dato (un artículo del blog que cita «AUMA, p. 16»):
 * arriba de la ficha, ese dato o los de esa página, con su cifra, su frase literal y el enlace a la
 * página del documento. **La ficha no lista los datos** (Iván, 2026-09-25): esto es solo lo enlazado.
 *
 * Al llegar, se desplaza hasta aquí y se resalta un momento. Si el dato ya no está (retirado, de un
 * lote anterior), no se enseña nada: la ficha sola sigue sirviendo.
 */
export function DatoEnlazado({
  slug,
  filtro,
  urlDocumento,
  idioma,
}: {
  readonly slug: string;
  readonly filtro: { readonly hecho: string } | { readonly pagina: number };
  readonly urlDocumento: string | null;
  readonly idioma: Idioma;
}) {
  const t = diccionarioDe(idioma).fuentes.enlazado;
  const [citas, setCitas] = useState<readonly CitaDeFicha[] | null>(null);
  const caja = useRef<HTMLElement>(null);
  const clave = 'pagina' in filtro ? `p${String(filtro.pagina)}` : filtro.hecho;

  useEffect(() => {
    let vigente = true;
    void pedirCitas(slug, filtro).then(c => {
      if (vigente) setCitas(c);
    });
    return () => {
      vigente = false;
    };
    // `clave` resume el filtro: un objeto nuevo en cada render no debe volver a pedir.
  }, [slug, clave]);

  useEffect(() => {
    if (citas === null || citas.length === 0 || caja.current === null) return;
    const quieto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? true;
    caja.current.scrollIntoView({ block: 'start', behavior: quieto ? 'auto' : 'smooth' });
    caja.current.focus({ preventScroll: true });
  }, [citas]);

  if (citas === null || citas.length === 0) return null;
  const pagina = citas[0]?.pagina ?? null;
  const enlacePagina = urlDocumento === null || pagina === null ? null : `${urlDocumento.split('#')[0] ?? urlDocumento}#page=${String(pagina)}`;

  return (
    <section
      ref={caja}
      id={pagina === null ? undefined : `p${String(pagina)}`}
      tabIndex={-1}
      aria-labelledby='dato-enlazado'
      className='dato-enlazado scroll-mt-24 rounded-lg border border-(--gold) bg-(--chat-bg) p-5 outline-none'
    >
      <h2 id='dato-enlazado' className='m-0 mb-3 font-(family-name:--text) text-[13px] font-semibold tracking-[0.04em] text-(--ink-soft) uppercase'>
        {pagina === null ? t.titulo : t.tituloPagina.replace('{p}', String(pagina))}
      </h2>
      <ul className='m-0 flex list-none flex-col gap-4 p-0'>
        {citas.map(c => (
          <li key={c.id} className='flex flex-col gap-1.5'>
            {c.cifra === null ? null : (
              <span className='font-(family-name:--serif) text-[26px] leading-none font-bold text-(--teal-deep)'>{c.cifra}</span>
            )}
            {c.cita === null ? (
              <span className='font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.sinFrase}</span>
            ) : (
              <blockquote className='m-0 border-l-2 border-(--line-strong) pl-3 font-(family-name:--serif) text-[17px] leading-[1.5] text-(--ink) italic'>
                <q>{c.cita}</q>
              </blockquote>
            )}
            <span lang='en' className='font-(family-name:--text) text-[13.5px] leading-[1.45] text-(--ink-faint)'>
              {c.afirmacion}
            </span>
          </li>
        ))}
      </ul>
      {enlacePagina === null ? null : (
        <a href={enlacePagina} {...ENLACE_A_EDITOR} className='mt-4 inline-block font-(family-name:--text) text-[14px] text-(--teal-deep)'>
          {t.abrirPagina.replace('{p}', String(pagina))}
        </a>
      )}
    </section>
  );
}
